// profileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { userInfoService } from "../../services/userInfoService";
import { atpService } from "../../services/atpService";
import { updateQualExpPercentageToZero } from "./authSlice";

// Define initial state for profile
const initialState = {
  profileData: {
    personal: null,
    residential: null,
    qualification: [],
    experience: [],
  },
  loading: false,
  qualificationLoading: false, // Separate loading state for qualifications
  experienceLoading: false, // Separate loading state for experience
  error: null,
  profileSettingsLoading: false, // Separate loading state for profile settings page
};

// An asynchronous thunk for fetching profile data from multiple APIs
export const fetchProfileData = createAsyncThunk(
  "profile/fetchProfileData",
  async (_, { rejectWithValue }) => {
    try {
      const [personal, residential, qualification, experience] =
        await Promise.all([
          userInfoService.getUserProfileInformation(),
          userInfoService.getAddressInformation(),
          userInfoService.getQualifications(),
          userInfoService.getUserExperience(),
        ]);

      // Process the responses and combine them as needed
      const combinedData = {
        personal: personal?.data?.data,
        residential: residential?.data?.data,
        qualification: qualification?.data?.data,
        experience: experience?.data?.data,
      };

      return combinedData;
    } catch (error) {
      toast.error("Failed to fetch profile data.");
      return rejectWithValue(error.response.data);
    }
  }
);

// An asynchronous thunk for fetching profile settings data from multiple APIs
export const fetchProfileSettingsData = createAsyncThunk(
  "profile/fetchProfileSettingsData",
  async (_, { rejectWithValue }) => {
    try {
      const [personal, residential, resume] = await Promise.all([
        userInfoService.getUserProfileInformation(),
        userInfoService.getAddressInformation(),
        userInfoService.getResume(),
      ]);

      // Process the responses and combine them as needed
      const combinedData = {
        personal: personal?.data?.data,
        residential: residential?.data?.data,
        resume: resume?.data?.data,
      };

      return combinedData;
    } catch (error) {
      toast.error("Failed to fetch profile data.");
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProfileSettingsData = createAsyncThunk(
  "profile/updateProfileSettings",
  async (data, { dispatch }) => {
    try {
      dispatch(setProfileSettingsLoading(true)); // Set qualification loading state
      const response = await userInfoService.updateProfileSettings(data);
      toast.success(response?.data?.message);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      throw error;
    } finally {
      dispatch(setProfileSettingsLoading(false)); // Reset qualification loading state
    }
  }
);

export const fetchPersonalProfileInformation = createAsyncThunk(
  "profile/personalProfileInformation",
  async (data, { dispatch }) => {
    try {
      const response = await userInfoService.getUserProfileInformation();
      return response?.data?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message);
      throw error;
    }
  }
);

// Synchronous thunks for qualification API calls
export const getQualificationsDetails = createAsyncThunk(
  "profile/getQualificationsDetails",
  async (_, { dispatch }) => {
    try {
      dispatch(setQualificationLoading(true)); // Set qualification loading state
      const response = await userInfoService.getQualifications();
      const data = response?.data?.data;
      if (data?.length === 0) {
        dispatch(updateQualExpPercentageToZero(["qualifications"]));
      }
      return data;
    } catch (error) {
      toast.error(error?.response?.data?.message);
      throw error;
    } finally {
      dispatch(setQualificationLoading(false)); // Reset qualification loading state
    }
  }
);

export const deleteQualification = createAsyncThunk(
  "profile/deleteQualification",
  async (id, { dispatch }) => {
    try {
      dispatch(setQualificationLoading(true)); // Set qualification loading state
      const response = await userInfoService.deleteQualification(id);
      toast.success(response?.data?.message);
      dispatch(getQualificationsDetails()); // Refresh qualifications after deletion
    } catch (error) {
      toast.error(error?.response?.data?.message);
      throw error;
    } finally {
      dispatch(setQualificationLoading(false)); // Reset qualification loading state
    }
  }
);

export const editQualification = createAsyncThunk(
  "profile/editQualification",
  async (id, { dispatch }) => {
    try {
      dispatch(setQualificationLoading(true)); // Set qualification loading state
      const response = await userInfoService.getQualificationById(id);
      const data = response?.data?.data;
      return data;
    } catch (error) {
      toast.error(error?.response?.data?.message);
      throw error;
    } finally {
      dispatch(setQualificationLoading(false)); // Reset qualification loading state
    }
  }
);

// An asynchronous thunk for fetching experiences details
export const getExperiencesDetails = createAsyncThunk(
  "userInfo/getExperiencesDetails",
  async (_, { dispatch }) => {
    try {
      dispatch(handleExperienceLoading(true));
      const response = await userInfoService.getUserExperience();
      const data = response?.data?.data;
      if (data?.length === 0) {
        dispatch(updateQualExpPercentageToZero(["work_experiences"]));
      }
      return data;
    } catch (error) {
      toast.error("Failed to fetch experiences details.");
    } finally {
      dispatch(handleExperienceLoading(false));
    }
  }
);

export const deleteExperience = createAsyncThunk(
  "userInfo/deleteExperience",
  async (id, { dispatch }) => {
    try {
      dispatch(handleExperienceLoading(true));
      await userInfoService.deleteUserExperience(id);
      dispatch(getExperiencesDetails());
      toast.success("Experience deleted successfully.");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      dispatch(handleExperienceLoading(false));
    }
  }
);

// An asynchronous thunk for handling editing of experience
export const editExperience = createAsyncThunk(
  "userInfo/editExperience",
  async (id, { dispatch }) => {
    try {
      dispatch(handleExperienceLoading(true));
      const response = await userInfoService.getUserExperienceById(id);
      const data = response?.data?.data;
      return data;
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      dispatch(handleExperienceLoading(false));
    }
  }
);

// An asynchronous thunk for fetching atp profile
export const fetchAtpProfile = createAsyncThunk(
  "userInfo/fetchAtpProfile",
  async (id, { dispatch }) => {
    try {
      const response = await atpService.getAtpPofile();
      const data = response?.data?.data;
      return data;
    } catch (error) {
      toast.error(error.response.data.message);
    }
  }
);

// Create the profile slice
const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    // Define additional reducers if needed
    setQualificationLoading: (state, action) => {
      state.qualificationLoading = action.payload;
    },
    handleExperienceLoading(state, action) {
      state.experienceLoading = action.payload;
    },
    setProfileSettingsLoading(state, action) {
      state.profileSettingsLoading = action.payload;
    },
    logoutprofile: (state, action) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.qualificationLoading = true;
        state.experienceLoading = true;
      })
      .addCase(fetchProfileData.fulfilled, (state, action) => {
        state.loading = false;
        state.profileData = action.payload;
        state.qualificationLoading = false;
        state.experienceLoading = false;
      })
      .addCase(fetchProfileData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.qualificationLoading = false;
        state.experienceLoading = false;
      })
      // profile settings start
      .addCase(fetchProfileSettingsData.pending, (state) => {
        state.profileSettingsLoading = true;
        state.error = null;
      })
      .addCase(fetchProfileSettingsData.fulfilled, (state, action) => {
        state.profileData.personal = action.payload.personal;
        state.profileData.residential = action.payload.residential;
        state.profileSettingsLoading = false;
      })
      .addCase(fetchProfileSettingsData.rejected, (state, action) => {
        state.error = action.payload;
        state.profileSettingsLoading = false;
      })
      // profile settings end
      .addCase(getQualificationsDetails.pending, (state) => {
        state.qualificationLoading = true; // Set loading state for qualifications
      })
      .addCase(getQualificationsDetails.fulfilled, (state, action) => {
        state.qualificationLoading = false; // Reset loading state for qualifications
        state.profileData.qualification = action.payload;
      })
      .addCase(getQualificationsDetails.rejected, (state, action) => {
        state.qualificationLoading = false; // Reset loading state for qualifications
      })
      .addCase(deleteQualification.pending, (state) => {
        state.qualificationLoading = true; // Set loading state for qualifications
      })
      .addCase(deleteQualification.rejected, (state, action) => {
        state.qualificationLoading = false; // Reset loading state for qualifications
        // Handle error if needed
      })
      .addCase(editQualification.pending, (state) => {
        state.qualificationLoading = true; // Set loading state for qualifications
      })
      .addCase(editQualification.rejected, (state, action) => {
        state.qualificationLoading = false; // Reset loading state for qualifications
        // Handle error if needed
      })
      .addCase(getExperiencesDetails.pending, (state) => {
        state.experienceLoading = true;
        state.error = null;
      })
      .addCase(getExperiencesDetails.fulfilled, (state, action) => {
        state.experienceLoading = false;
        state.profileData.experience = action.payload;
      })
      .addCase(getExperiencesDetails.rejected, (state, action) => {
        state.experienceLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteExperience.pending, (state) => {
        state.experienceLoading = true; // Set loading state for qualifications
      })
      .addCase(deleteExperience.fulfilled, (state) => {
        state.experienceLoading = false; // Set loading state for qualifications
      })
      .addCase(deleteExperience.rejected, (state, action) => {
        state.experienceLoading = false; // Reset loading state for qualifications
        // Handle error if needed
      })
      .addCase(editExperience.pending, (state) => {
        state.experienceLoading = true; // Set loading state for qualifications
      })
      .addCase(editExperience.fulfilled, (state) => {
        state.experienceLoading = false; // Set loading state for qualifications
      })
      .addCase(editExperience.rejected, (state, action) => {
        state.experienceLoading = false; // Reset loading state for qualifications
        // Handle error if needed
      })
      .addCase(fetchAtpProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAtpProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profileData = action.payload;
      })
      .addCase(fetchAtpProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export the profile actions and reducer
export const {
  setQualificationLoading,
  logoutprofile,
  handleExperienceLoading,
  setProfileSettingsLoading,
} = profileSlice.actions;
export default profileSlice.reducer;
